import {castDraft, type Draft} from 'immer'

import isEmpty from 'lodash/isEmpty'
import {createSelector} from 'reselect'

import type {NewPipeline, Pipeline} from '../../services/pipelinesApi.types'
import type {Integration} from '../../types'
import {isYamlStoredInRepo} from '../EditPipelinePage.utils'

import type {
  PipelineDebug,
  PipelineDebugState,
  PipelineDependencyDetailsState,
  PipelineDraft,
  PipelineDraftState,
  PipelineFormParams,
  PipelineYamlDraft,
  PipelineYamlDraftState,
  PipelineYamlValidity,
  PipelineYamlValidityState,
} from './EditPipelinePage.slices.types'

import type {PipelineId} from 'src/types'
import {DEFAULT_NAME as DEFAULT_BRANCH_NAME} from 'src/utils/branchNames'

// Pipeline draft slice state is two levels deep: pipeline id -> branch -> entry.
// The three helper functions below encapsulate the indexing logic, so callers
// never have to write `state[id]?.[branch]` directly.

// Read-only lookup; returns `undefined` when the pipeline or branch is absent.
export const getPipelineDraftEntry = (
  state: PipelineDraftState,
  id: string,
  branch = DEFAULT_BRANCH_NAME,
) => state[id]?.[branch]

// Get-or-create: returns the existing entry or inserts an empty one and returns it.
export const ensurePipelineDraftEntry = (
  state: Draft<PipelineDraftState>,
  id: string,
  branch = DEFAULT_BRANCH_NAME,
) => {
  const entryByBranch = (state[id] ??= {})
  return (entryByBranch[branch] ??= {})
}

// Overwrites the whole entry for a branch.
export const setPipelineDraftEntry = (
  state: Draft<PipelineDraftState>,
  id: string,
  value: PipelineDraft,
  branch = DEFAULT_BRANCH_NAME,
) => {
  const entryByBranch = (state[id] ??= {})
  entryByBranch[branch] = castDraft(value)
}

/**
 * Whether the entry has visual changes: deletions, renames or reorderings. Lives here rather than
 * next to the other change selectors because the pipelines reducer needs it too, and it only ever
 * reads a single draft entry.
 */
export const isDraftChanged = createSelector(
  (state?: PipelineDraft) => state?.deleted,
  (state?: PipelineDraft) => state?.renamed,
  (state?: PipelineDraft) => state?.reordered,
  (state?: PipelineDraft) => state?.draft?.settings?.jobs,
  (deleted, renamed, reordered, jobs) =>
    Boolean(
      deleted ||
      renamed ||
      reordered ||
      Object.values(jobs ?? {}).some(job =>
        job.features?.some(feature => feature.deleted === true),
      ),
    ),
)

export function getJob(
  state: PipelineDraftState,
  id: string,
  jobId: string,
  branch = DEFAULT_BRANCH_NAME,
) {
  return getPipelineDraftEntry(state, id, branch)?.draft?.settings.jobs?.[jobId]
}

// Debug slice choke-point, nested per pipeline + branch (default → `<default>`).
export const getDebugEntry = (
  state: PipelineDebugState,
  id: PipelineId,
  branch = DEFAULT_BRANCH_NAME,
) => state[id]?.[branch]

export const ensureDebugEntry = (
  state: Draft<PipelineDebugState>,
  id: PipelineId,
  branch = DEFAULT_BRANCH_NAME,
): Draft<PipelineDebug> => {
  const entryByBranch = (state[id] ??= {})
  return (entryByBranch[branch] ??= {})
}

// pipelineYamlDraft slice read choke-point; reused by the selector getter.
export const getYamlDraftEntry = (
  state: PipelineYamlDraftState,
  id: PipelineId,
  branch = DEFAULT_BRANCH_NAME,
) => state[id]?.[branch]

// Reducers mutate fields, so use get-or-create.
export const ensureYamlDraftEntry = (
  state: Draft<PipelineYamlDraftState>,
  id: PipelineId,
  branch = DEFAULT_BRANCH_NAME,
): Draft<PipelineYamlDraft> => {
  const entryByBranch = (state[id] ??= {})
  return (entryByBranch[branch] ??= {})
}

// pipelineYamlValidity slice read choke-point; reused by the selector getter.
export const getYamlValidityEntry = (
  state: PipelineYamlValidityState,
  id: PipelineId,
  branch = DEFAULT_BRANCH_NAME,
) => state[id]?.[branch]

// Reducers replace the whole entry.
export const setYamlValidityEntry = (
  state: Draft<PipelineYamlValidityState>,
  id: PipelineId,
  value: PipelineYamlValidity,
  branch = DEFAULT_BRANCH_NAME,
) => {
  const entryByBranch = (state[id] ??= {})
  entryByBranch[branch] = value
}

// Read choke-point; returns the per-branch dependency-details map or `undefined`.
export const getDependencyDetailsEntry = (
  state: PipelineDependencyDetailsState,
  id: PipelineId,
  branch = DEFAULT_BRANCH_NAME,
) => state[id]?.[branch]

export const ensureDependencyDetailsEntry = (
  state: Draft<PipelineDependencyDetailsState>,
  id: PipelineId,
  branch = DEFAULT_BRANCH_NAME,
) => {
  const entryByBranch = (state[id] ??= {})
  return (entryByBranch[branch] ??= {})
}

export function getNextIntegrationId(draft: NewPipeline, type: string) {
  const otherIntegrationsIds: string[] =
    draft.integrations
      ?.filter(integration => integration.type === type)
      .map(integration => integration.id)
      .filter(id => id != null) ?? []

  const idNumber = Math.max(
    ...(otherIntegrationsIds
      .map(id => parseInt(id.substring(type.length + 1), 10))
      .filter(num => !isNaN(num)) ?? []),
    -1,
  )

  return `${type}_${idNumber + 1}`
}

export const filterIntegrations = (
  integrations: Integration[] | undefined,
  id: string | undefined,
  displayName: string | undefined | null,
) =>
  integrations?.filter(integration => {
    if (typeof integration === 'object') {
      return integration.id === id
    } else {
      return integration === displayName
    }
  })

export function deleteIfEmpty<T>(object: T | undefined, key: keyof T) {
  if (object != null && isEmpty(object[key])) {
    delete object[key]
    return true
  }
  return false
}

export function deleteNestedIfEmpty<T>(
  object: Record<string, Record<string, T>> | undefined,
  id: string,
  branch: string,
) {
  const byBranch = object?.[id]
  if (byBranch != null) {
    deleteIfEmpty(byBranch, branch)
    deleteIfEmpty(object, id)
  }
}

export function getPipelineFormKey({
  jobId,
  id,
  type,
  branch,
}: Omit<PipelineFormParams, 'pipelineId'>) {
  return `${branch}::${jobId ?? ''}::${type}::${id ?? ''}`
}

export function getStepSecretKey(jobId: string, stepIndex: number, name: string) {
  return `${jobId}::${stepIndex}::${name}`
}

// Server-stored pipelines have a single draft, always keyed by the default branch
export const getEffectivePipelineBranch = (pipeline: Pipeline, branch?: string) =>
  isYamlStoredInRepo(pipeline.versionedSettings)
    ? (branch ?? DEFAULT_BRANCH_NAME)
    : DEFAULT_BRANCH_NAME
